import React from 'react';
import { motion } from 'framer-motion';
import { Smartphone, Users, Brain, Shield, Zap, Globe, BarChart3, Heart } from 'lucide-react';
import './Features.css';

const Features = () => {
  const features = [
    {
      icon: <Smartphone size={32} />,
      title: "Mobile-First Design",
      description: "Built for the modern lifestyle with seamless mobile experience across all devices.",
      category: "Technology"
    },
    {
      icon: <Users size={32} />,
      title: "Community-Driven",
      description: "Foster meaningful connections with fitness enthusiasts worldwide through our social platform.",
      category: "Community"
    },
    {
      icon: <Brain size={32} />,
      title: "AI-Powered Coaching",
      description: "Personalized workout plans and nutrition guidance powered by advanced machine learning.",
      category: "Innovation"
    },
    {
      icon: <Shield size={32} />,
      title: "Privacy & Security",
      description: "Enterprise-grade security ensuring your health data remains private and protected.",
      category: "Security"
    },
    {
      icon: <Zap size={32} />,
      title: "Real-Time Analytics",
      description: "Track progress with detailed insights and performance metrics updated in real-time.",
      category: "Analytics"
    },
    {
      icon: <Globe size={32} />,
      title: "Global Network",
      description: "Access to certified coaches and trainers from around the world, 24/7.",
      category: "Accessibility"
    },
    {
      icon: <BarChart3 size={32} />,
      title: "Data-Driven Insights",
      description: "Make informed decisions about your fitness journey with comprehensive analytics.",
      category: "Analytics"
    },
    {
      icon: <Heart size={32} />,
      title: "Holistic Wellness",
      description: "Beyond just workouts - complete wellness including nutrition, recovery, and mental health.",
      category: "Wellness"
    }
  ];

  const categories = ["Technology", "Community", "Innovation", "Security", "Analytics", "Accessibility", "Wellness"];

  return (
    <section className="features" id="features">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">What Makes Us Different</h2>
          <p className="section-subtitle">
            AllFitHive isn't just another fitness app – we're redefining the entire experience
          </p>
        </motion.div>

        <motion.div
          className="differentiators"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="differentiator-card primary">
            <div className="differentiator-icon">
              <Users size={48} />
            </div>
            <h3>Community First</h3>
            <p>
              Unlike traditional fitness apps that focus solely on individual workouts, 
              AllFitHive builds communities around shared goals, creating accountability 
              and motivation that drives real results.
            </p>
          </div>

          <div className="differentiator-card secondary">
            <div className="differentiator-icon">
              <Brain size={48} />
            </div>
            <h3>AI That Actually Works</h3>
            <p>
              Our AI doesn't just suggest generic workouts – it learns from your progress, 
              adapts to your preferences, and evolves with your fitness journey.
            </p>
          </div>

          <div className="differentiator-card accent">
            <div className="differentiator-icon">
              <Globe size={48} />
            </div>
            <h3>Global Accessibility</h3>
            <p>
              We're breaking down geographical barriers, connecting users with world-class 
              coaches regardless of location, making premium fitness guidance accessible to all.
            </p>
          </div>
        </motion.div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="feature-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <div className="feature-header">
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <span className="feature-category">{feature.category}</span>
              </div>
              <h4>{feature.title}</h4>
              <p>{feature.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="competitive-advantage"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <h3>Competitive Advantage</h3>
          <div className="advantage-grid">
            <div className="advantage-item">
              <div className="advantage-header">
                <h4>Traditional Fitness Apps</h4>
                <div className="vs-badge">vs</div>
                <h4>AllFitHive</h4>
              </div>
              <div className="comparison">
                <div className="traditional">
                  <ul>
                    <li>Generic workout plans</li>
                    <li>Isolated experience</li>
                    <li>Limited personalization</li>
                    <li>No community support</li>
                    <li>Basic tracking</li>
                  </ul>
                </div>
                <div className="allfithive">
                  <ul>
                    <li>AI-powered personalization</li>
                    <li>Community-driven motivation</li>
                    <li>Adaptive learning</li>
                    <li>Global network support</li>
                    <li>Advanced analytics</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
